import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import toast from 'react-hot-toast';

export default function UserProfilePage() {
  const [user, setUser] = useState<any>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then((r: { data: { user: User | null } }) => {
      setUser(r.data.user);
      setName(r.data.user?.user_metadata?.name || '');
      setEmail(r.data.user?.email || '');
    });
  }, []);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const currentName = user?.user_metadata?.name || '';
    const currentEmail = user?.email || '';

    const isNameChanged = trimmedName !== currentName;
    const isEmailChanged = trimmedEmail !== currentEmail;

    if (!isNameChanged && !isEmailChanged) {
      toast('No profile changes to save.');
      return;
    }

    if (!trimmedEmail) {
      toast.error('Email is required.');
      return;
    }

    setProfileLoading(true);
    const updatePayload: { data?: { name: string }; email?: string } = {};
    if (isNameChanged) {
      updatePayload.data = { name: trimmedName };
    }
    if (isEmailChanged) {
      updatePayload.email = trimmedEmail;
    }

    const { data, error } = await supabase.auth.updateUser(updatePayload);
    setProfileLoading(false);

    if (error) {
      // Common when current or new domain is blocked (e.g. example.com) or email confirm is required
      toast.error(error.message);
      return;
    }

    if (isEmailChanged) {
      toast.success(
        'Email update requested. Confirm via the link sent to the new address (if required by your project settings), then sign out and back in.'
      );
    } else {
      toast.success('Name updated!');
    }

    const nextUser = data?.user;
    setName(trimmedName);
    setEmail(nextUser?.email || trimmedEmail);
    setUser((prev: any) => ({
      ...(nextUser || prev),
      user_metadata: {
        ...((nextUser || prev)?.user_metadata || {}),
        name: trimmedName,
      },
    }));
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPasswordLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Password updated!');
      setNewPassword('');
    }
  };

  return (
    <div className="max-w-lg mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">User Profile</h1>
      <form onSubmit={handleProfileUpdate} className="mb-6">
        <label className="block mb-1 font-medium">Name</label>
        <input
          className="border rounded px-3 py-2 w-full mb-2"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <label className="block mb-1 font-medium">Email</label>
        <input
          type="email"
          className="border rounded px-3 py-2 w-full mb-2"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded"
          disabled={profileLoading}
        >
          {profileLoading ? 'Saving...' : 'Update Profile'}
        </button>
      </form>
      <form onSubmit={handlePasswordChange}>
        <label className="block mb-1 font-medium">New Password</label>
        <input
          type="password"
          className="border rounded px-3 py-2 w-full mb-2"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
        />
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded"
          disabled={passwordLoading || !newPassword}
        >
          {passwordLoading ? 'Saving...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
} 